import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { CheckCircle, ArrowDown } from "lucide-react";
import welcomeEmailMobile from "@/assets/welcome_email_mobile.png";
import welcomeEmailDesktop from "@/assets/welcome_email_desktop.png";
import { trackHyrosEvent } from "../../lib/hyros";

export const Confirmation = () => {
  const location = useLocation();
  const [email, setEmail] = useState<string>("sample@test.com");

  useEffect(() => {
    // Extract email from location state if available
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [location]);

  useEffect(() => {
    trackHyrosEvent("Payment Success", 0, {
      email: email,
      createdAt: new Date(),
    });
  }, []);

  return (
    <AuthLayout>
      <Card className="w-full max-w-3xl">
        <CardContent className="p-6">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-green-100 rounded-full p-2 mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-center">Order Confirmed</h1>
            <p className="text-center text-gray-700 mt-2">
              Congratulations, your 30 day access pass is unlocked!
            </p>
          </div>

          <div className="space-y-6 text-gray-700">
            <p>
              Before you start using the app, let's quickly cover a few things
              so you're good to go.
            </p>

            <p>
              Firstly, your order will appear on your credit card statement as
              'Options AI Premium', in case you get confused 🙂
            </p>

            <p>
              Secondly, if you're having problems with your order please don't
              hesitate to get in touch via:{" "}
              <a
                href="mailto:support@optionsapp.com"
                className="text-purple-600 hover:underline"
              >
                support@optionsapp.com
              </a>{" "}
              we will be happy to assist you with any issues you may have. Your
              satisfaction is our top priority!
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="font-semibold">IMPORTANT!</p>
              <p>
                You entered the email ({email}) when placing your order. This is
                where we have sent your login information. If this email is
                incorrect, please contact support immediately at:{" "}
                <a
                  href="mailto:support@optionsapp.com"
                  className="text-purple-600 hover:underline"
                >
                  support@optionsapp.com
                </a>{" "}
                and we'll get you set up right away.
              </p>
            </div>

            <div>
              <h2 className="font-bold text-xl mb-3">Here's what's next...</h2>
              <p className="mb-4">
                You can access the app via the button below. But before you head
                over there, we want to verify that you can receive emails from
                us, so we've sent a couple of emails with important info about
                your purchase...
              </p>
              <p className="mb-2">
                Look out for this welcome email from{" "}
                <a
                  href="mailto:support@optionsapp.com"
                  className="text-purple-600 hover:underline"
                >
                  support@optionsapp.com
                </a>{" "}
                - which contains your login details to the email address you
                ordered with:
              </p>

              <div className="flex flex-col items-center my-6">
                <ArrowDown className="w-8 h-8 text-gray-400 mb-4" />
                <div className="border border-gray-300 rounded-md w-full max-w-lg overflow-hidden">
                  {/* Mobile email screenshot - only shown on small screens */}
                  <img
                    src={welcomeEmailMobile}
                    alt="Welcome email screenshot"
                    className="w-full h-auto block md:hidden"
                  />
                  {/* Desktop email screenshot - only shown on medium screens and up */}
                  <img
                    src={welcomeEmailDesktop}
                    alt="Welcome email screenshot"
                    className="w-full h-auto hidden md:block"
                  />
                </div>
              </div>

              <p className="mb-6">
                If you can't see it, please check your spam folder because our
                emails sometimes end up there, or in the "promotions" tab (if
                you use Gmail).
              </p>

              <div className="flex flex-col items-center mb-6">
                <h3 className="font-semibold mb-4">Access the App here:</h3>
                <Button
                  className="w-full max-w-md bg-purple-600 text-white hover:bg-purple-700 py-6 text-lg hover:text-white"
                  asChild
                  variant="custom"
                >
                  <Link to="https://chat.optionsapp.com/login">
                    Access Options AI
                  </Link>
                </Button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <p className="mb-2">
                  You can access Options AI on your mobile, tablet or computer.
                  Here's the login link for quick reference:
                </p>
                <p className="mb-4">
                  <a
                    href="https://chat.optionsapp.com/login"
                    className="text-purple-600 hover:underline break-all"
                  >
                    https://chat.optionsapp.com/login
                  </a>
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Your username is your email address.</li>
                  <li>
                    And your password has been emailed to you. To change your
                    password to something more memorable, in the Options App
                    Sidebar tap your name {">>"} My Account {">>"} Change
                    Password. Or use the 'Forgot password?' link on the login
                    page.
                  </li>
                </ul>
              </div>

              <p className="mt-6">That's it for now. Enjoy!</p>
              <p className="font-medium">Options App Team</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="h-20"></div>
    </AuthLayout>
  );
};
